import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { useMemo, useState, useRef, useEffect } from 'react';

const ROUTE_META: Record<string, { label: string; icon: string }> = {
  '/':             { label: 'Progressões',   icon: 'ri-bar-chart-line'  },
  '/funcionarios': { label: 'Funcionários',  icon: 'ri-team-line'       },
  '/siadap':       { label: 'SIADAP',        icon: 'ri-award-line'      },
  '/historico':    { label: 'Histórico',     icon: 'ri-history-line'    },
  '/resumo':       { label: 'Resumo',        icon: 'ri-pie-chart-2-line'},
  '/admin':        { label: 'Administração',  icon: 'ri-settings-3-line'      },
  '/perfil':       { label: 'O meu perfil',  icon: 'ri-user-settings-line'   },
};

export default function TopHeader() {
  const { profile, signOut, user } = useAuth();
  const { theme } = useTheme();
  const { settings } = useAppSettings();
  const { activeMunicipio, municipios } = useActiveMunicipio();
  const location = useLocation();
  const anoAtual = new Date().getFullYear();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const meta = ROUTE_META[location.pathname] ?? { label: 'Página', icon: 'ri-pages-line' };

  const initials = useMemo(() => {
    const name = profile?.full_name || profile?.email || user?.email || '';
    return name.split(/[\s@]/).filter(Boolean).slice(0, 2).map((n: string) => n[0]?.toUpperCase() ?? '').join('');
  }, [profile, user]);

  const userName = profile?.full_name || profile?.email || user?.email || '';
  const userEmail = profile?.full_name ? (profile?.email ?? '') : '';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate('/login');
  };

  return (
    <div style={{
      height: '52px',
      minHeight: '52px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #f0f0f0',
      zIndex: 20,
    }}>
      {/* Esquerda: página atual */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <i className={meta.icon} style={{ fontSize: '14px', color: '#a1a1aa' }} />
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#3f3f46' }}>{meta.label}</span>
        <span style={{ color: '#d4d4d8', fontSize: '14px', margin: '0 2px' }}>/</span>
        <span style={{ fontSize: '13px', color: '#a1a1aa' }}>{theme.nomeCamara || 'Câmara Municipal'}</span>
      </div>

      {/* Direita: badge + utilizador */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Badge módulo ativo — só mostra se houver mais de 1 módulo disponível */}
        {activeMunicipio && municipios.length > 1 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '11px', fontWeight: 600,
            color: theme.sidebarAccent,
            backgroundColor: `${theme.sidebarAccent}12`,
            border: `1px solid ${theme.sidebarAccent}30`,
            padding: '3px 10px 3px 7px', borderRadius: '99px',
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '16px', height: '16px', borderRadius: '4px',
              backgroundColor: theme.sidebarAccent,
              fontSize: '8px', fontWeight: 700, color: '#fff', lineHeight: 1,
              flexShrink: 0,
            }}>
              {activeMunicipio.sigla?.slice(0, 2) ?? '?'}
            </span>
            {activeMunicipio.nome}
          </span>
        )}
        {/* Badge ciclo dinâmico — escondido no módulo de formação */}
        {!location.pathname.startsWith('/formacao') && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            fontSize: '11px', fontWeight: 600, color: '#a1a1aa',
            backgroundColor: '#fafafa', border: '1px solid #f0f0f0',
            padding: '3px 10px', borderRadius: '99px',
          }}>
            <i className="ri-calendar-line" style={{ fontSize: '11px' }} />
            {settings.ciclo_siadap ? `Ciclo ${settings.ciclo_siadap}` : 'Ciclo 2023–2024'}
          </span>
        )}
        {/* Badge ano atual */}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          fontSize: '11px', fontWeight: 600, color: '#a1a1aa',
          backgroundColor: '#fafafa', border: '1px solid #f0f0f0',
          padding: '3px 10px', borderRadius: '99px',
        }}>
          <i className="ri-time-line" style={{ fontSize: '11px' }} />
          {anoAtual}
        </span>

        {/* User dropdown */}
        <div ref={ref} style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '5px 10px 5px 6px',
              borderRadius: '8px', border: 'none', background: 'transparent',
              cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f4f4f5')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: `${theme.sidebarAccent}22`,
              color: theme.sidebarAccent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 700,
            }}>
              {initials || <i className="ri-user-line" style={{ fontSize: '12px' }} />}
            </div>
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#3f3f46', lineHeight: 1.3, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userName}
              </p>
              {userEmail && (
                <p style={{ fontSize: '10px', color: '#a1a1aa', lineHeight: 1.3, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userEmail}
                </p>
              )}
            </div>
            <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} style={{ fontSize: '14px', color: '#a1a1aa' }} />
          </button>

          {open && (
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
              width: '200px', backgroundColor: '#ffffff',
              border: '1px solid #f0f0f0', borderRadius: '12px',
              overflow: 'hidden', zIndex: 50,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f9f9f9' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#18181b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</p>
                {userEmail && <p style={{ fontSize: '11px', color: '#a1a1aa', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</p>}
              </div>
              <div style={{ padding: '4px 0' }}>
                <NavLink
                  to="/perfil"
                  onClick={() => setOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 16px', fontSize: '13px', color: '#52525b',
                    textDecoration: 'none', transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <i className="ri-user-settings-line" style={{ fontSize: '14px' }} />
                  O meu perfil
                </NavLink>
                <NavLink
                  to="/admin"
                  onClick={() => setOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 16px', fontSize: '13px', color: '#52525b',
                    textDecoration: 'none', transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fafafa')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <i className="ri-settings-3-line" style={{ fontSize: '14px' }} />
                  Administração
                </NavLink>
                <button
                  onClick={handleSignOut}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 16px', fontSize: '13px', color: '#ef4444',
                    background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', transition: 'background 0.1s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <i className="ri-logout-box-line" style={{ fontSize: '14px' }} />
                  Terminar Sessão
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
